import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  DataTable,
  SortableHeadCell,
  TABLE_ICON_CLASS,
  TableCell,
  TableEmptyRow,
  TableFacts,
  TableHead,
  TableHeadCell,
  TableItem,
  TableRow,
} from "@/components/data-table";
import { FilterBar } from "@/components/filter-bar";
import { FilterToggle } from "@/components/filter-toggle";
import { Highlight } from "@/components/highlight";
import { MultiSelect } from "@/components/multi-select";
import { ToolIcon } from "@/components/tool-icon";
import { ToolName } from "@/components/tool-name";
import type { ConnectorToolGroup } from "@/lib/api";
import { formatNumber } from "@/lib/format";
import type { Period } from "@/lib/period";
import { matchesSearch } from "@/lib/search";
import { factLine } from "@/lib/table-facts";
import { sortRows, useUrlTableSort } from "@/lib/table-sort";
import { toolLabel, useToolTitles } from "@/lib/tool-titles";
import { cn } from "@/lib/utils";

import { RowLink } from "../parts";
import { callsLink, formatDuration, formatFailures, type ToolRow } from "../utils";

interface Props {
  rows: ToolRow[];
  /** The catalogue's groups, in the dashboard's language, for the group filter. */
  groups: readonly ConnectorToolGroup[];
  days: Period;
}

/** Tool, calls, failures, average duration, the way to its calls. */
const COLUMNS = 5;

const SORT_COLUMNS = {
  tool: "asc",
  calls: "desc",
  errors: "desc",
  mean: "desc",
} as const;

type SortKey = keyof typeof SORT_COLUMNS;

/**
 * Calls per tool in the period, then the tools without calls, muted: each
 * tool with its icon, its title and name, and what it does, so the table
 * says what every tool does. Every column sorts (kept in the URL); a search,
 * the tool's group and "Alleen met fouten" filter it. A row leads to the call
 * log filtered on its tool. One duration, the average (Snelheid has the
 * median and the p95). On a phone the calls stay right, and the failures and
 * the duration move under the tool.
 */
export function ToolTable({ rows, groups, days }: Props) {
  const { t } = useTranslation();
  const titles = useToolTitles();
  const [search, setSearch] = useState("");
  const [chosenGroups, setChosenGroups] = useState<string[]>([]);
  const [onlyErrors, setOnlyErrors] = useState(false);
  const sort = useUrlTableSort<SortKey>({
    columns: SORT_COLUMNS,
    initial: { key: "calls", dir: "desc" },
    names: { sort: "toolSort", dir: "toolDir" },
  });

  if (rows.length === 0) {
    return (
      <p className="text-muted rounded-lg border py-8 text-center text-sm">
        {t("analytics.toolsEmpty")}
      </p>
    );
  }

  const shownGroups = groups.filter((group) => rows.some((row) => row.group === group.id));
  const filtered = search !== "" || chosenGroups.length > 0 || onlyErrors;
  const reset = () => {
    setSearch("");
    setChosenGroups([]);
    setOnlyErrors(false);
  };
  const matching = rows.filter(
    (row) =>
      matchesSearch([row.title, row.summary, row.tool], search) &&
      (chosenGroups.length === 0 || (row.group !== null && chosenGroups.includes(row.group))),
  );
  const shown = sortRows(
    matching.filter((row) => !onlyErrors || row.errors > 0),
    sort.sort,
    (row, key) => {
      switch (key) {
        case "tool":
          return toolLabel(titles, row.tool);
        case "calls":
          return row.calls;
        case "errors":
          return row.errors;
        case "mean":
          return row.mean_duration_ms;
      }
    },
  );
  const groupLabel =
    chosenGroups.length === 0
      ? t("tools.filter.allGroups")
      : shownGroups
          .filter((group) => chosenGroups.includes(group.id))
          .map((group) => group.title)
          .join(", ");

  return (
    <div className="space-y-3">
      <FilterBar
        search={{ value: search, onChange: setSearch, label: t("tools.filter.search") }}
        columns={["wide", "auto"]}
        filtered={filtered}
        onReset={reset}
      >
        <MultiSelect
          size="sm"
          className="w-full"
          options={shownGroups.map((group) => ({ value: group.id, label: group.title }))}
          value={chosenGroups}
          onChange={setChosenGroups}
          label={groupLabel}
          name={t("tools.filter.group")}
          allLabel={t("tools.filter.allGroups")}
        />
        <FilterToggle
          label={t("analytics.onlyWithErrors")}
          count={matching.filter((row) => row.errors > 0).length}
          pressed={onlyErrors}
          onPressedChange={setOnlyErrors}
          className="w-full justify-between"
        />
      </FilterBar>
      <DataTable label={t("analytics.byTool")}>
        <TableHead>
          <SortableHeadCell {...sort.head("tool")}>{t("analytics.tool")}</SortableHeadCell>
          {/* In the phone layout its arrow may go under its name, so the table fits. */}
          <SortableHeadCell {...sort.head("calls")} align="right" wrap>
            {t("analytics.calls")}
          </SortableHeadCell>
          <SortableHeadCell {...sort.head("errors")} align="right" narrow="hide">
            {t("analytics.errors")}
          </SortableHeadCell>
          <SortableHeadCell {...sort.head("mean")} align="right" narrow="hide">
            {t("analytics.meanDuration")}
          </SortableHeadCell>
          <TableHeadCell className="w-px">
            <span className="sr-only">{t("analytics.callsColumn")}</span>
          </TableHeadCell>
        </TableHead>
        <tbody>
          {shown.map((row) => {
            const unused = row.calls === 0;
            const label = toolLabel(titles, row.tool);
            return (
              <TableRow key={row.tool}>
                <TableCell>
                  <TableItem
                    icon={
                      <ToolIcon
                        name={row.tool}
                        className={cn(TABLE_ICON_CLASS, unused && "opacity-60")}
                      />
                    }
                    title={<ToolName name={row.tool} titles={titles} search={search} />}
                    summary={row.summary && <Highlight text={row.summary} query={search} />}
                    facts={
                      <TableFacts>
                        {factLine([
                          t("analytics.failuresFact", {
                            failures: formatFailures(row.errors, row.calls),
                          }),
                          row.mean_duration_ms != null &&
                            t("analytics.meanFact", {
                              duration: formatDuration(row.mean_duration_ms),
                            }),
                        ])}
                      </TableFacts>
                    }
                    muted={unused}
                  />
                </TableCell>
                <TableCell align="right" className={cn("tabular-nums", unused && "text-muted")}>
                  {formatNumber(row.calls)}
                </TableCell>
                <TableCell
                  align="right"
                  narrow="hide"
                  className={cn("whitespace-nowrap tabular-nums", row.errors === 0 && "text-muted")}
                >
                  {formatFailures(row.errors, row.calls)}
                </TableCell>
                <TableCell
                  align="right"
                  narrow="hide"
                  className="text-muted whitespace-nowrap tabular-nums"
                >
                  {formatDuration(row.mean_duration_ms)}
                </TableCell>
                <TableCell align="right" className="py-1 pr-1">
                  {unused ? (
                    <span aria-hidden="true" className="inline-block w-8 pointer-coarse:w-10" />
                  ) : (
                    <RowLink
                      to={callsLink({ tool: row.tool }, days)}
                      label={t("analytics.callsOf", { name: label })}
                    />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
          {shown.length === 0 && (
            <TableEmptyRow colSpan={COLUMNS} onReset={reset}>
              {t("tools.filter.empty")}
            </TableEmptyRow>
          )}
        </tbody>
      </DataTable>
    </div>
  );
}
